import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

// Helper component for text inputs with auto-save
const TextQuestion = ({ id, question, value, onChange, multiline = false, maxLength, placeholder }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const Component = multiline ? Textarea : Input;

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <label className="block text-base font-medium text-slate-800 mb-3">{question}</label>
      <Component
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || "Enter your response..."}
        maxLength={maxLength}
        rows={multiline ? 4 : undefined}
        className="w-full"
        data-testid={`input-${id}`}
      />
      {maxLength && (
        <p className="text-xs text-muted-foreground mt-2 text-right">
          {localValue.length}/{maxLength} characters
        </p>
      )}
    </div>
  );
};

// Helper component for single choice questions with "Other" option
const SingleChoiceQuestion = ({ id, question, options, value, onChange, hasOther = true }) => {
  const [otherText, setOtherText] = useState("");
  const isOtherSelected = value && value.startsWith("Other:");
  
  useEffect(() => {
    if (isOtherSelected) {
      setOtherText(value.replace("Other: ", ""));
    }
  }, [value, isOtherSelected]);

  const handleOptionChange = (option) => {
    if (option === "Other") {
      onChange(`Other: ${otherText}`);
    } else {
      onChange(option);
    }
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    onChange(`Other: ${text}`);
  };

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-4">{question}</p>
      <RadioGroup 
        value={isOtherSelected ? "Other" : (value || "")} 
        onValueChange={handleOptionChange}
        className="space-y-3"
      >
        {options.map((option, index) => (
          <div
            key={index}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
              (value === option || (option === "Other" && isOtherSelected))
                ? 'border-[#003366] bg-blue-50' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => handleOptionChange(option)}
          >
            <RadioGroupItem
              value={option}
              id={`${id}-${index}`}
              className="border-slate-300 text-[#003366]"
            />
            <Label
              htmlFor={`${id}-${index}`}
              className="cursor-pointer flex-1 text-sm"
            >
              {option}
            </Label>
          </div>
        ))}
        {hasOther && (
          <div
            className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors ${
              isOtherSelected ? 'border-[#003366] bg-blue-50' : 'border-slate-200'
            }`}
          >
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleOptionChange("Other")}>
              <RadioGroupItem
                value="Other"
                id={`${id}-other`}
                className="border-slate-300 text-[#003366]"
              />
              <Label htmlFor={`${id}-other`} className="cursor-pointer text-sm">
                Other (please specify)
              </Label>
            </div>
            {isOtherSelected && (
              <Input
                value={otherText}
                onChange={(e) => handleOtherTextChange(e.target.value)}
                placeholder="Please specify..."
                className="ml-7 mt-2"
                autoFocus
              />
            )}
          </div>
        )}
      </RadioGroup>
    </div>
  );
};

// Helper component for table-based education/certification inputs
const TableQuestion = ({ id, question, columns, rows, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value || rows.map(() => ({})));

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setLocalValue(value);
    } else {
      setLocalValue(rows.map(() => ({})));
    }
  }, [value, rows]);

  const updateRow = (rowIndex, field, fieldValue) => {
    const newValue = [...localValue];
    newValue[rowIndex] = { ...newValue[rowIndex], [field]: fieldValue };
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    onChange(localValue);
  };

  return (
    <div className="question-card bg-white rounded-lg border p-6 mb-6" data-testid={`question-${id}`}>
      <p className="text-base font-medium text-slate-800 mb-4">{question}</p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50">
              {columns.map((col, idx) => (
                <th key={idx} className="border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-600">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((_, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="border border-slate-200 p-1">
                    <Input
                      value={localValue[rowIndex]?.[col.field] || ""}
                      onChange={(e) => updateRow(rowIndex, col.field, e.target.value)}
                      onBlur={handleBlur}
                      placeholder={col.placeholder || ""}
                      className="border-0 text-sm h-9"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Fill in as many rows as applicable</p>
    </div>
  );
};

export const FoundationSection = ({ responses, onSave, profile }) => {
  const getValue = (questionId) => responses?.[questionId];

  return (
    <div className="space-y-2">
      {/* Section intro */}
      <Card className="bg-[#f0f7ff] border-[#003366]/20 mb-8">
        <CardContent className="pt-6">
          <p className="text-sm text-[#003366]">
            This section helps us understand your background, education, and professional credentials. 
            Please provide accurate information about your career journey before Sagicor.
          </p>
        </CardContent>
      </Card>

      {/* 1.1 Professional Background */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          1.1 Professional Background
        </h3>

        <TextQuestion
          id="1_1_q1"
          question="1. Before joining Sagicor, what was your most recent role? (Company & Job Title)"
          value={getValue("1_1_q1")}
          onChange={(val) => onSave("1_1_q1", val)}
          multiline
          maxLength={500}
          placeholder="e.g., Company: ABC Corp, Job Title: Systems Administrator"
        />

        <TextQuestion
          id="1_1_q2"
          question="2. Briefly describe your career journey before Sagicor:"
          value={getValue("1_1_q2")}
          onChange={(val) => onSave("1_1_q2", val)}
          multiline
          maxLength={2000}
        />

        <SingleChoiceQuestion
          id="1_1_q3"
          question="3. Have you worked in insurance or financial services before Sagicor?"
          options={[
            "Yes, extensively (5+ years in industry)",
            "Yes, some experience (1-5 years)",
            "Limited exposure",
            "No prior insurance/financial services experience"
          ]}
          value={getValue("1_1_q3")}
          onChange={(val) => onSave("1_1_q3", val)}
        />

        <SingleChoiceQuestion
          id="1_1_q4"
          question="4. Total years of professional experience in IT/Technology:"
          options={[
            "Less than 5 years",
            "5-10 years",
            "10-15 years",
            "15-20 years",
            "20-25 years",
            "More than 25 years"
          ]}
          value={getValue("1_1_q4")}
          onChange={(val) => onSave("1_1_q4", val)}
        />
      </div>

      {/* 1.2 Education */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          1.2 Education
        </h3>

        <SingleChoiceQuestion
          id="1_2_q5"
          question="5. Highest level of education completed:"
          options={[
            "High School / GED",
            "Some College / Technical Training",
            "Associate Degree / Diploma",
            "Bachelor's Degree",
            "Master's Degree",
            "PhD / Doctorate"
          ]}
          value={getValue("1_2_q5")}
          onChange={(val) => onSave("1_2_q5", val)}
          hasOther
        />

        <TableQuestion
          id="1_2_q6"
          question="6. Please provide details of your educational background:"
          columns={[
            { field: "degree", label: "Degree / Diploma", placeholder: "e.g., Bachelor's" },
            { field: "field", label: "Field of Study", placeholder: "e.g., Computer Science" },
            { field: "institution", label: "Institution", placeholder: "e.g., University" },
            { field: "year", label: "Year", placeholder: "e.g., 2005" }
          ]}
          rows={[1, 2, 3]}
          value={getValue("1_2_q6")}
          onChange={(val) => onSave("1_2_q6", val)}
        />
      </div>

      {/* 1.3 Professional Certifications */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          1.3 Professional Certifications
        </h3>

        <TableQuestion
          id="1_3_q7"
          question="7. List ALL current professional certifications you hold:"
          columns={[
            { field: "name", label: "Certification Name", placeholder: "e.g., AWS Solutions Architect" },
            { field: "issuer", label: "Issuing Body", placeholder: "e.g., Amazon" },
            { field: "obtained", label: "Year Obtained", placeholder: "e.g., 2022" },
            { field: "expiration", label: "Expiration", placeholder: "e.g., 2025" }
          ]}
          rows={[1, 2, 3, 4, 5]}
          value={getValue("1_3_q7")}
          onChange={(val) => onSave("1_3_q7", val)}
        />

        <TextQuestion
          id="1_3_q8"
          question="8. List any certifications you held in the past that have since expired:"
          value={getValue("1_3_q8")}
          onChange={(val) => onSave("1_3_q8", val)}
          multiline
          maxLength={1000}
        />
      </div>

      {/* 1.4 Your Sagicor Journey */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6 pb-2 border-b border-slate-200">
          1.4 Your Sagicor Journey
        </h3>

        <TextQuestion
          id="1_4_q9"
          question={`9. What attracted you to join Sagicor as ${profile?.job_title || 'your role'}?`}
          value={getValue("1_4_q9")}
          onChange={(val) => onSave("1_4_q9", val)}
          multiline
          maxLength={1000}
        />

        <TextQuestion
          id="1_4_q10"
          question="10. What has kept you at Sagicor?"
          value={getValue("1_4_q10")}
          onChange={(val) => onSave("1_4_q10", val)}
          multiline
          maxLength={1000}
        />

        <TextQuestion
          id="1_4_q11"
          question="11. What are the most significant technology changes you have experienced during your tenure?"
          value={getValue("1_4_q11")}
          onChange={(val) => onSave("1_4_q11", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="1_4_q12"
          question="12. What institutional knowledge do you possess that would be difficult to replace?"
          value={getValue("1_4_q12")}
          onChange={(val) => onSave("1_4_q12", val)}
          multiline
          maxLength={1500}
        />
      </div>
    </div>
  );
};

export default FoundationSection;
